/** @file One VAO, one dynamic vertex buffer and one index buffer holding every chunk at a fixed slot. */
import { RENDER_LAYOUT } from '@pose2d/geometry/renderBuffers';
import type { RenderBuffers } from '@pose2d/geometry/types';
import { invariant } from '@pose2d/std/invariant';

/** Bytes per vertex: RENDER_LAYOUT.VERTEX_WORDS 32-bit words. */
const STRIDE = RENDER_LAYOUT.VERTEX_WORDS * 4;
/** Attribute byte offsets in a vertex (RenderBuffers words 0-1 x, y; 2-3 u, v; 4 light bytes R, G, B, A). */
const UV_OFFSET = 2 * 4;
const LIGHT_OFFSET = 4 * 4;

/** Where one chunk lives in the two buffers and which of its versions were uploaded. */
interface Slot {
  chunk: RenderBuffers | null;
  version: number;
  topology: number;
  vertexByte: number;
  indexByte: number;
  vertexCap: number;
  indexCap: number;
}

/** GPU geometry for an ordered chunk list: a frame calls sync once, then bindChunk per chunk. */
export interface ChunkStore {
  /** Uploads what changed: vertices when new or `version` moved, indices when new or `topologyVersion` moved. */
  sync(chunks: readonly RenderBuffers[]): void;
  /** Points attributes 0-2 at chunk `i`'s vertices and returns the byte offset of its indices. */
  bindChunk(i: number): number;
  /** Deletes the buffers and the VAO; with `lost` only forgets them (the context already dropped them). */
  dispose(lost: boolean): void;
}

const align4 = (n: number): number => (n + 3) & ~3;

/** True when the slots no longer match the chunk count or some chunk's capacity. */
function needsLayout(slots: readonly Slot[], chunks: readonly RenderBuffers[]): boolean {
  if (slots.length !== chunks.length) return true;
  return chunks.some((c, i) => slots[i]?.vertexCap !== c.vertexF32.byteLength || slots[i].indexCap !== align4(c.indices.byteLength));
}

/** Assigns consecutive slots, reallocates both buffers and marks every slot empty (full re-upload). */
function layout(gl: WebGL2RenderingContext, chunks: readonly RenderBuffers[]): Slot[] {
  let vertexByte = 0;
  let indexByte = 0;
  const slots = chunks.map((c): Slot => {
    const slot = { chunk: null, version: 0, topology: 0, vertexByte, indexByte, vertexCap: c.vertexF32.byteLength, indexCap: align4(c.indices.byteLength) };
    vertexByte += slot.vertexCap;
    indexByte += slot.indexCap;
    return slot;
  });
  gl.bufferData(gl.ARRAY_BUFFER, vertexByte, gl.DYNAMIC_DRAW);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indexByte, gl.DYNAMIC_DRAW);
  return slots;
}

/** Uploads one chunk into its slot (vertices and/or indices as their versions require); never zero-length. */
function upload(gl: WebGL2RenderingContext, slot: Slot, chunk: RenderBuffers): void {
  const fresh = slot.chunk !== chunk;
  if ((fresh || slot.version !== chunk.version) && chunk.vertexCount > 0) {
    gl.bufferSubData(gl.ARRAY_BUFFER, slot.vertexByte, chunk.vertexF32, 0, chunk.vertexCount * RENDER_LAYOUT.VERTEX_WORDS);
  }
  if ((fresh || slot.topology !== chunk.topologyVersion) && chunk.indexCount > 0) {
    gl.bufferSubData(gl.ELEMENT_ARRAY_BUFFER, slot.indexByte, chunk.indices, 0, chunk.indexCount);
  }
  slot.chunk = chunk;
  slot.version = chunk.version;
  slot.topology = chunk.topologyVersion;
}

/** Creates the VAO (attributes 0 position, 1 uv, 2 light enabled; index buffer bound inside it) and buffers. */
export function createChunkStore(gl: WebGL2RenderingContext): ChunkStore {
  const vao = gl.createVertexArray();
  const vertexBuffer = gl.createBuffer();
  const indexBuffer = gl.createBuffer();
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
  for (const attribute of [0, 1, 2]) gl.enableVertexAttribArray(attribute);
  let slots: Slot[] = [];
  return {
    sync(chunks) {
      gl.bindVertexArray(vao);
      gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
      if (needsLayout(slots, chunks)) slots = layout(gl, chunks);
      chunks.forEach((chunk, i) => {
        const slot = slots[i];
        if (slot) upload(gl, slot, chunk);
      });
    },
    bindChunk(i) {
      const slot = slots[i];
      invariant(slot, `chunk ${i} was not synced`);
      gl.vertexAttribPointer(0, 2, gl.FLOAT, false, STRIDE, slot.vertexByte);
      gl.vertexAttribPointer(1, 2, gl.FLOAT, false, STRIDE, slot.vertexByte + UV_OFFSET);
      gl.vertexAttribPointer(2, 4, gl.UNSIGNED_BYTE, true, STRIDE, slot.vertexByte + LIGHT_OFFSET);
      return slot.indexByte;
    },
    dispose(lost) {
      slots = [];
      if (lost) return;
      gl.deleteBuffer(vertexBuffer);
      gl.deleteBuffer(indexBuffer);
      gl.deleteVertexArray(vao);
    },
  };
}
